/**
 * Delivery method + pickup point picker in the cart (snippets/kyn-delivery-picker.liquid).
 *
 * The choice is saved as cart attributes (copied to the order by Shopify):
 *   "Dostawa" – the rate name, "Punkt odbioru" – point code, "Punkt odbioru - adres" – point name,
 *   "Przewoźnik" – map service. Checkout is blocked until a method (and, for pickup methods, a point)
 *   is chosen. The cart page and the drawer can both hold a picker, so the state lives in the module.
 *
 * Checkout (Basic plan) can't show cart attributes, but it shows line item properties under each product.
 * So the choice is also written to every cart line as a visible property, the only place the customer sees
 * it inside checkout: "Punkt odbioru" / "Pickup point" with the point, or "Dostawa" / "Delivery" with the
 * courier. A courier cart then reads "Dostawa: Kurier" next to a pickup rate, so the mismatch shows before
 * paying. The cart itself hides these properties (snippets/cart-products.liquid); the picker shows them.
 */

import { sectionRenderer } from '@theme/section-renderer';

const MAP_SCRIPT = 'https://furgonetka.pl/js/dist/map/map.js';
const ATTR = { method: 'Dostawa', code: 'Punkt odbioru', name: 'Punkt odbioru - adres', carrier: 'Przewoźnik' };
/**
 * Line property keys in every storefront language; keep in sync with kyn.delivery.line_property /
 * line_method and snippets/cart-products.liquid.
 */
const LINE_KEYS = ['Punkt odbioru', 'Pickup point', 'Dostawa', 'Delivery'];

/** @type {{ method: string, code: string, name: string, carrier: string, needsPoint: boolean }} */
const state = { method: '', code: '', name: '', carrier: '', needsPoint: false };
let initialised = false;

/** @type {Set<KynDeliveryPicker>} */
const pickers = new Set();

/** @type {Promise<void> | null} */
let mapScript = null;

function loadMap() {
  if (window.Furgonetka) return Promise.resolve();
  mapScript ??= new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = MAP_SCRIPT;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      mapScript = null;
      reject(new Error('Furgonetka map failed to load'));
    };
    document.head.append(script);
  });
  return mapScript;
}

/** @type {HTMLDialogElement | null} */
let dialogElement = null;

/**
 * One shared map dialog on <body>, outside the cart drawer, so re-renders of the drawer can't remove it.
 * @param {string} title @param {string} subtitle @param {string} closeLabel
 */
function mapDialog(title, subtitle, closeLabel) {
  if (!dialogElement) {
    dialogElement = document.createElement('dialog');
    dialogElement.className = 'kyn-delivery-map';
    dialogElement.setAttribute('aria-labelledby', 'kyn-delivery-map-title');
    dialogElement.innerHTML = `
      <div class="kyn-delivery-map__head">
        <div class="kyn-delivery-map__titles">
          <span class="kyn-delivery-map__kicker" data-map-subtitle></span>
          <h2 class="kyn-delivery-map__title" id="kyn-delivery-map-title" data-map-title></h2>
        </div>
        <button type="button" class="kyn-delivery-map__close" data-map-close>
          <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M2 2l12 12M14 2L2 14" stroke="currentColor" stroke-width="1.5"/></svg>
          <span class="visually-hidden" data-map-close-label></span>
        </button>
      </div>
      <div class="kyn-delivery-map__body" id="kyn-delivery-map-container" data-map-container></div>`;
    dialogElement.querySelector('[data-map-close]')?.addEventListener('click', () => dialogElement?.close());
    // Backdrop click closes; Escape must not also reach the drawer underneath.
    dialogElement.addEventListener('click', (event) => {
      if (event.target === dialogElement) dialogElement.close();
    });
    dialogElement.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') event.stopPropagation();
    });
    document.body.append(dialogElement);
  }
  /** @type {HTMLElement} */ (dialogElement.querySelector('[data-map-title]')).textContent = title;
  /** @type {HTMLElement} */ (dialogElement.querySelector('[data-map-subtitle]')).textContent = subtitle;
  /** @type {HTMLElement} */ (dialogElement.querySelector('[data-map-close-label]')).textContent = closeLabel;
  return dialogElement;
}

function complete() {
  if (!state.method) return false;
  return !state.needsPoint || Boolean(state.code);
}

function syncDocument() {
  document.documentElement.dataset.kynDelivery = complete() ? 'complete' : 'incomplete';
  for (const picker of pickers) picker.render();
}

/** The point as one line: "Paczkomat InPost 24/7 · Paczkomat WAW121H, Jana Kazimierza 23, 01-248 Warszawa". */
function pointLabel() {
  if (!state.needsPoint || !state.code) return '';
  const place = state.name.includes(state.code) ? state.name : [state.code, state.name].filter(Boolean).join(', ');
  return [state.method, place].filter(Boolean).join(' · ');
}

let lineKey = LINE_KEYS[0];
let lineMethodKey = LINE_KEYS[2];

/** The visible name of the chosen method ("Kurier" / "Courier"), read from a picker. */
function methodName() {
  for (const picker of pickers) {
    const radio = [...picker.querySelectorAll('input[type="radio"]')].find(
      (input) => /** @type {HTMLInputElement} */ (input).value === state.method
    );
    const name = radio?.closest('label')?.querySelector('.kyn-delivery__option-name')?.textContent?.trim();
    if (name) return name;
  }
  return state.method;
}

/** The delivery properties every cart line should carry; empty while nothing is complete. */
function lineProperties() {
  /** @type {Record<string, string>} */
  const wanted = {};
  if (state.needsPoint) {
    const label = pointLabel();
    if (label) wanted[lineKey] = label;
  } else if (state.method) {
    wanted[lineMethodKey] = methodName();
  }
  return wanted;
}

/** @type {Promise<void> | null} */
let syncing = null;
let syncAgain = false;

/**
 * Writes the delivery choice to every cart line (or removes it), one /cart/change.js per line that differs.
 * Each response is the new cart: changing properties can merge two lines, so indexes are re-read every time.
 */
function syncLines() {
  if (syncing) {
    syncAgain = true;
    return syncing;
  }
  syncing = (async () => {
    let changed = false;
    try {
      do {
        syncAgain = false;
        const wanted = lineProperties();
        let cart = await fetch(`${Theme.routes.cart_url}.js`).then((response) => response.json());
        for (let guardCount = 0; guardCount < 50; guardCount++) {
          const index = cart.items.findIndex((item) => {
            const properties = item.properties || {};
            return LINE_KEYS.some((key) => (properties[key] ?? '') !== (wanted[key] ?? ''));
          });
          if (index === -1) break;
          const item = cart.items[index];
          const properties = { ...(item.properties || {}) };
          for (const key of LINE_KEYS) delete properties[key];
          Object.assign(properties, wanted);
          const response = await fetch(`${Theme.routes.cart_change_url}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ line: index + 1, quantity: item.quantity, properties }),
          });
          if (!response.ok) break;
          cart = await response.json();
          changed = true;
        }
      } while (syncAgain);
    } catch (error) {
      console.error(error);
    } finally {
      syncing = null;
    }
    // Line keys changed: re-render the cart lines so quantity buttons and remove links use the new ones.
    if (changed) {
      for (const items of document.querySelectorAll('cart-items-component')) {
        const { sectionId } = /** @type {HTMLElement} */ (items).dataset;
        if (!sectionId) continue;
        sectionRenderer
          .renderSection(sectionId, { cache: false, mode: items.hasAttribute('data-drawer') ? 'hydration' : 'full' })
          .catch(() => {});
      }
    }
  })();
  return syncing;
}

/** Saves the current state to the cart; empty values remove the attribute. */
function save() {
  return saveAttributes().then(syncLines);
}

function saveAttributes() {
  return fetch(`${Theme.routes.cart_update_url}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      attributes: {
        [ATTR.method]: state.method,
        [ATTR.code]: state.needsPoint ? state.code : '',
        [ATTR.name]: state.needsPoint ? state.name : '',
        [ATTR.carrier]: state.needsPoint ? state.carrier : '',
      },
    }),
  }).catch((error) => console.error(error));
}

/** Blocks the checkout buttons (cart page, drawer, sticky bar) until delivery is complete. */
function guard(event) {
  if (!pickers.size || (complete() && !syncing)) return;
  const target = /** @type {HTMLElement} */ (event.target);
  const isCheckout =
    event.type === 'submit'
      ? event.submitter?.getAttribute('name') === 'checkout'
      : target.closest?.('button[name="checkout"]');
  if (!isCheckout) return;

  // Delivery is complete but the lines are still being updated: wait, then press the same button again.
  if (complete()) {
    if (!syncing) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const button = event.type === 'submit' ? event.submitter : target.closest('button[name="checkout"]');
    syncing.then(() => /** @type {HTMLElement | null} */ (button)?.click());
    return;
  }

  event.preventDefault();
  event.stopImmediatePropagation();
  const visible = [...pickers].find((picker) => picker.offsetParent) ?? [...pickers][0];
  visible?.showError();
}

class KynDeliveryPicker extends HTMLElement {
  connectedCallback() {
    if (!initialised) {
      initialised = true;
      state.method = this.dataset.method || '';
      state.code = this.dataset.code || '';
      state.name = this.dataset.name || '';
      lineKey = this.dataset.lineKey || lineKey;
      lineMethodKey = this.dataset.lineMethodKey || lineMethodKey;
      document.addEventListener('click', guard, true);
      document.addEventListener('submit', guard, true);
      // Lines added, removed or changed elsewhere (product form, cart quantity): bring the point property up to date.
      document.addEventListener('shopify:cart:lines-update', (event) => {
        /** @type {any} */ (event).promise?.then(() => syncLines()).catch(() => {});
      });
      queueMicrotask(() => syncLines());
    }
    pickers.add(this);

    const checked = this.#radios().find((radio) => radio.value === state.method);
    // A method saved earlier that is no longer offered (e.g. "Kurier") must be chosen again.
    if (state.method && !checked && this.#radios().length) {
      state.method = '';
      state.code = '';
      state.name = '';
    }
    state.needsPoint = checked ? Boolean(checked.dataset.services) : state.needsPoint;
    if (checked) state.carrier ||= checked.dataset.services || '';

    for (const radio of this.#radios()) {
      radio.addEventListener('change', this.#onMethod);
      radio.addEventListener('click', this.#onReselect);
    }
    this.querySelector('[data-open-map]')?.addEventListener('click', this.#openMap);
    syncDocument();
  }

  disconnectedCallback() {
    pickers.delete(this);
  }

  #radios() {
    return /** @type {HTMLInputElement[]} */ ([...this.querySelectorAll('input[type="radio"]')]);
  }

  #selected() {
    return this.#radios().find((radio) => radio.value === state.method);
  }

  #onMethod = (event) => {
    const radio = /** @type {HTMLInputElement} */ (event.currentTarget);
    const services = radio.dataset.services || '';
    const sameCarrier = services && services === state.carrier;
    state.method = radio.value;
    state.needsPoint = Boolean(services);
    if (!sameCarrier) {
      state.code = '';
      state.name = '';
    }
    state.carrier = services;
    save();
    syncDocument();
    if (state.needsPoint && !state.code) this.#openMap();
  };

  /** Clicking the pickup option that is already selected (no `change` event) reopens the map. */
  #onReselect = (event) => {
    const radio = /** @type {HTMLInputElement} */ (event.currentTarget);
    if (radio.value === state.method && radio.dataset.services) this.#openMap();
  };

  #openMap = async () => {
    const radio = this.#selected();
    if (!radio?.dataset.services) return;
    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-open-map]'));
    const title = radio.dataset.pointTypes === 'parcel_machine' ? this.dataset.labelMapLocker : this.dataset.labelMapPoint;
    const dialog = mapDialog(title || '', radio.closest('label')?.querySelector('.kyn-delivery__option-name')?.textContent || '', this.dataset.labelClose || '');
    // The widget attaches a shadow root to its container, which can't be attached twice:
    // every opening gets a fresh container element.
    const previous = /** @type {HTMLElement} */ (dialog.querySelector('[data-map-container]'));
    const container = /** @type {HTMLElement} */ (previous.cloneNode(false));
    previous.replaceWith(container);
    dialog.onclose = () => button?.focus();
    // A modal dialog opened after the cart drawer sits above it in the top layer.
    dialog.showModal();

    try {
      await loadMap();
      const pointTypes = (radio.dataset.pointTypes || '').split(',').filter(Boolean);
      new window.Furgonetka.Map({
        apiKey: this.dataset.mapKey,
        courierServices: radio.dataset.services.split(','),
        ...(pointTypes.length ? { availableServiceTypes: pointTypes } : {}),
        container: `#${container.id}`,
        callback: (params) => {
          const point = params?.point ?? params;
          if (!point?.code) return;
          state.code = String(point.code);
          state.name = String(point.name || '');
          state.carrier = String(point.type || radio.dataset.services);
          save();
          syncDocument();
          dialog.close();
        },
      }).show();
      this.#setError('');
    } catch (error) {
      console.error(error);
      dialog.close();
      this.#setError(this.dataset.labelMapError || '');
    }
  };

  /** @param {string} message */
  #setError(message) {
    const error = /** @type {HTMLElement | null} */ (this.querySelector('[data-error]'));
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
    this.toggleAttribute('data-invalid', Boolean(message));
  }

  showError() {
    this.#setError(state.method ? this.dataset.labelMissingPoint || '' : this.dataset.labelMissingMethod || '');
    this.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    const focusTarget = state.method ? this.querySelector('[data-open-map]') : this.#radios()[0];
    /** @type {HTMLElement | null} */ (focusTarget)?.focus({ preventScroll: true });
  }

  render() {
    for (const radio of this.#radios()) radio.checked = radio.value === state.method;

    const point = /** @type {HTMLElement | null} */ (this.querySelector('[data-point]'));
    if (point) point.hidden = !state.needsPoint;
    const code = this.querySelector('[data-point-code]');
    const name = this.querySelector('[data-point-name]');
    if (code) code.textContent = state.code;
    if (name) name.textContent = state.code ? state.name : '';
    const button = this.querySelector('[data-open-map]');
    if (button) button.textContent = (state.code ? this.dataset.labelChange : this.dataset.labelChoose) || '';
    this.toggleAttribute('data-has-point', Boolean(state.code));

    if (complete()) this.#setError('');
  }
}

if (!customElements.get('kyn-delivery-picker')) customElements.define('kyn-delivery-picker', KynDeliveryPicker);
